import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, Typography, Grid } from '@mui/material';
import { Volume2, RotateCcw, Globe } from 'lucide-react';

interface AACSymbol {
  id: string;
  label: string;
  emoji: string;
  shortcut: string;
}

const MULTILINGUAL_BOARD_SYMBOLS: Record<string, AACSymbol[]> = {
  'en-US': [
    { id: '1', label: 'I / ME', emoji: '🖐️', shortcut: '1' },
    { id: '2', label: 'WANT', emoji: '❤️', shortcut: '2' },
    { id: '3', label: 'WATER', emoji: '💧', shortcut: '3' },
    { id: '4', label: 'FOOD', emoji: '🍎', shortcut: '4' },
    { id: '5', label: 'TOILET', emoji: '🚽', shortcut: '5' },
    { id: '6', label: 'HAPPY', emoji: '😊', shortcut: '6' },
    { id: '7', label: 'PAIN / HURT', emoji: '😢', shortcut: '7' },
    { id: '8', label: 'HOME', emoji: '🏠', shortcut: '8' },
  ],
  'hi-IN': [
    { id: '1', label: 'मैं / मुझे', emoji: '🖐️', shortcut: '1' },
    { id: '2', label: 'चाहिए', emoji: '❤️', shortcut: '2' },
    { id: '3', label: 'पानी', emoji: '💧', shortcut: '3' },
    { id: '4', label: 'खाना', emoji: '🍎', shortcut: '4' },
    { id: '5', label: 'शौचालय', emoji: '🚽', shortcut: '5' },
    { id: '6', label: 'खुश', emoji: '😊', shortcut: '6' },
    { id: '7', label: 'दर्द', emoji: '😢', shortcut: '7' },
    { id: '8', label: 'घर', emoji: '🏠', shortcut: '8' },
  ],
  'mr-IN': [
    { id: '1', label: 'मी / मला', emoji: '🖐️', shortcut: '1' },
    { id: '2', label: 'हवे', emoji: '❤️', shortcut: '2' },
    { id: '3', label: 'पाणी', emoji: '💧', shortcut: '3' },
    { id: '4', label: 'जेवण', emoji: '🍎', shortcut: '4' },
    { id: '5', label: 'शौचालय', emoji: '🚽', shortcut: '5' },
    { id: '6', label: 'आनंदी', emoji: '😊', shortcut: '6' },
    { id: '7', label: 'त्रास / दुखापत', emoji: '😢', shortcut: '7' },
    { id: '8', label: 'घर', emoji: '🏠', shortcut: '8' },
  ]
};

export const AACBoard: React.FC = () => {
  const { i18n } = useTranslation();
  const [selectedPhrase, setSelectedPhrase] = useState<string[]>([]);
  const [language, setLanguage] = useState('en-US');

  useEffect(() => {
    if (i18n.language === 'hi') setLanguage('hi-IN');
    else if (i18n.language === 'mr') setLanguage('mr-IN');
    else setLanguage('en-US');
  }, [i18n.language]);

  const symbols = MULTILINGUAL_BOARD_SYMBOLS[language] || MULTILINGUAL_BOARD_SYMBOLS['en-US'];

  const handleSelectSymbol = (symbol: AACSymbol) => {
    setSelectedPhrase((prev) => [...prev, symbol.label]);
  };

  const handleSpeakSentence = () => {
    if (selectedPhrase.length === 0) return;
    const text = selectedPhrase.join(' ');
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = language; // Explicitly set speech synthesis language!
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleClear = () => {
    setSelectedPhrase([]);
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-md border border-slate-200 space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
        <div>
          <Typography variant="h5" className="font-bold text-slate-800">
            AAC Augmentative Communication Board
          </Typography>
          <Typography variant="body2" className="text-slate-500">
            Touch, Switch Scan, or Keyboard (Keys 1-8) • Voice Language: <strong className="text-blue-600 font-mono">{language}</strong>
          </Typography>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg border border-slate-300 text-xs">
            <Globe className="w-4 h-4 text-slate-500 ml-1" />
            <select
              value={language}
              onChange={(e) => {
                const newLang = e.target.value;
                setLanguage(newLang);
                if (newLang.startsWith('hi')) i18n.changeLanguage('hi');
                else if (newLang.startsWith('mr')) i18n.changeLanguage('mr');
                else i18n.changeLanguage('en');
              }}
              className="bg-white border-0 font-bold text-slate-800 text-xs focus:ring-0"
            >
              <option value="en-US">English (US)</option>
              <option value="hi-IN">हिंदी (HI)</option>
              <option value="mr-IN">मराठी (MR)</option>
            </select>
          </div>

          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<Volume2 className="w-4 h-4" />}
            onClick={handleSpeakSentence}
            disabled={selectedPhrase.length === 0}
          >
            Speak All ({language})
          </Button>

          <Button 
            variant="outlined" 
            color="error" 
            startIcon={<RotateCcw className="w-4 h-4" />}
            onClick={handleClear}
            disabled={selectedPhrase.length === 0}
          >
            Clear
          </Button>
        </div>
      </div>

      {/* Spoken Phrase Preview Bar */}
      <div 
        className="p-4 bg-slate-100 rounded-lg border border-slate-300 min-h-[60px] flex items-center gap-2 overflow-x-auto"
        role="status"
        aria-live="polite"
      >
        <Typography variant="subtitle2" className="text-slate-400 font-medium mr-2">
          Constructed Phrase:
        </Typography>
        {selectedPhrase.length === 0 ? (
          <span className="text-slate-400 italic">Tap symbols below to build a sentence...</span>
        ) : (
          selectedPhrase.map((word, idx) => (
            <span key={idx} className="px-3 py-1 bg-blue-600 text-white rounded-full font-bold text-sm shadow">
              {word}
            </span>
          ))
        )}
      </div>

      {/* AAC Grid */}
      <Grid container spacing={2}>
        {symbols.map((symbol) => (
          <Grid item xs={6} sm={3} key={symbol.id}>
            <Card 
              className="cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all border-2 border-slate-200 text-center"
              onClick={() => handleSelectSymbol(symbol)}
              tabIndex={0}
              role="button"
              aria-label={`AAC Symbol ${symbol.label}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleSelectSymbol(symbol);
                }
              }}
            >
              <CardContent className="p-4">
                <div className="text-4xl mb-2">{symbol.emoji}</div>
                <Typography variant="subtitle1" className="font-bold text-slate-800">
                  {symbol.label}
                </Typography>
                <span className="text-xs text-slate-400 font-mono mt-1 block">Key: {symbol.shortcut}</span>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};
